import { ICoupon, ICouponType, TCouponSource, TCouponState } from '../model/ICoupon';
import api from './api';
import { IPagesResponse } from './IPagesResponse';

/** What the create and edit form sends. */
export interface ICouponForm {
  name: string;
  code: string;
  typeId: number;
  /** Percent off, or dollars off, depending on the type. */
  value1: number;
  /** The minimum order subtotal. Zero means any order. */
  value2: number;
  /** Only read for the part-restricted types. */
  partCategoryId: number;
  /** yyyy-MM-dd, the last day the coupon works. */
  expiresOn: string;
}

export interface ICouponQuery {
  page: number;
  size: number;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  source: TCouponSource;
  state: TCouponState;
  q?: string;
}

// --
// -- Every endpoint here is @PreAuthorize("hasRole('ADMIN')") on the backend,
// -- on the controller class rather than method by method. The page is gated to
// -- ADMIN too, but that is for the sake of the menu - the backend is what
// -- actually refuses anyone else.
// --
export const couponApi = api.injectEndpoints({
  endpoints: (build) => ({
    // GET /api/v1/coupons
    //
    // Source and state are separate filters because they are separate
    // questions - who made it, and whether it still works - and the view this
    // page opens on is a combination of the two.
    getCoupons: build.query<IPagesResponse<ICoupon[]>, ICouponQuery>({
      query: ({ sort, sortDir, q, ...params }) => ({
        url: 'coupons',
        params: {
          ...params,
          ...(q ? { q } : {}),
          ...(sort ? { sort: `${sort},${sortDir || 'asc'}` } : {}),
        },
      }),
      providesTags: ['Coupons'],
    }),

    // GET /api/v1/coupons/types
    //
    // Served rather than hardcoded: the storefront refuses any type not on
    // this list, and the flags say which value is a percentage and which types
    // need a part category.
    getCouponTypes: build.query<ICouponType[], void>({
      query: () => ({
        url: 'coupons/types',
      }),
    }),

    // PUT /api/v1/coupons
    createCoupon: build.mutation<ICoupon, ICouponForm>({
      query: (body) => ({
        url: 'coupons',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Coupons'],
    }),

    // POST /api/v1/coupons/{id}
    updateCoupon: build.mutation<ICoupon, { id: number } & ICouponForm>({
      query: ({ id, ...body }) => ({
        url: `coupons/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Coupons'],
    }),

    // DELETE /api/v1/coupons/{id} -- responds with plain text "ok"
    deleteCoupon: build.mutation<unknown, number>({
      query: (id) => ({
        url: `coupons/${id}`,
        method: 'DELETE',
        responseHandler: 'text',
      }),
      invalidatesTags: ['Coupons'],
    }),
  }),
});
